import { useState } from "react";
import { useDocumentData } from "react-firebase-hooks/firestore";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "~/components/loading/loading-spinner";
import { PaginationController } from "~/components/pagination-controller/pagination-controller";
import { EmptyText } from "~/components/empty/empty-text";
import { Text } from "~/components/text";
import { useOrganisation } from "~/contexts/organisation-provider";
import {
  getManuscriptProcessingReportDocument,
  ManuscriptProcessingReportWithId,
} from "~/integrations/firebase/collections/manuscript-processing-reporting";
import { ManuscriptProcessingFile } from "./components/manuscript-processing-file";
import { ManuscriptProcessingReportSheet } from "./components/manuscript-processing-report-sheet";
import { ManuscriptProcessingStatusTiles } from "./components/manuscript-processing-status-tiles";
import { useManuscriptProcessingReports } from "./hooks/use-manuscript-processing-reports";

export const OrganisationManuscriptProcessingPage = () => {
  const { t } = useTranslation("settings");
  const { id: organisationId } = useOrganisation();
  const { reports, loading, isFirstLoad, error, itemsCount, statusCounts } =
    useManuscriptProcessingReports({ organisationId });
  const [selectedReport, setSelectedReport] =
    useState<ManuscriptProcessingReportWithId | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [originalEntryId, setOriginalEntryId] = useState<string | null>(null);

  const selectReport = (report: ManuscriptProcessingReportWithId) => {
    setOriginalEntryId(null);
    setSelectedReport(report);
    setIsSheetOpen(true);
  };

  // The entry a duplicate points at usually sits on another page, so it is
  // read by id rather than looked up in the loaded list.
  const [originalEntry] = useDocumentData(
    originalEntryId
      ? getManuscriptProcessingReportDocument(originalEntryId)
      : undefined,
  );

  // Follow the live card, not the snapshot taken at click time. The fallback
  // outlives both the close animation and paging away.
  const openReport =
    (originalEntryId ? originalEntry : undefined) ??
    reports.find((report) => report.id === selectedReport?.id) ??
    selectedReport;

  const isEmpty =
    !loading && !error && reports.length === 0 && itemsCount === 0;
  const spinner = (
    <div className="flex w-full items-center justify-center pt-10">
      <LoadingSpinner />
    </div>
  );

  return (
    <div>
      <Text as="h2" variant="pageHeading" className="mb-2">
        {t("organisation.manuscriptProcessing.title")}
      </Text>
      <Text variant="subtle" className="max-w-2xl pb-4 text-pretty">
        {t("organisation.manuscriptProcessing.description")}
      </Text>

      {isFirstLoad && spinner}

      {error && (
        <div className="mt-8">
          <EmptyText
            title={t("organisation.manuscriptProcessing.failed.title")}
            text={t("organisation.manuscriptProcessing.failed.text")}
          />
        </div>
      )}

      {isEmpty && (
        <div className="mt-8">
          <EmptyText
            title={t("organisation.manuscriptProcessing.empty.title")}
            text={t("organisation.manuscriptProcessing.empty.text")}
          />
        </div>
      )}

      {!isFirstLoad && !isEmpty && !error && (
        <>
          <ManuscriptProcessingStatusTiles counts={statusCounts} />
          {loading ? (
            spinner
          ) : (
            <ul
              className="mt-6 flex flex-col gap-2"
              aria-label={t("organisation.manuscriptProcessing.listLabel")}
            >
              {reports.map((report) => (
                <ManuscriptProcessingFile
                  key={report.id}
                  report={report}
                  onSelect={() => selectReport(report)}
                />
              ))}
            </ul>
          )}
          <div className="mt-4">
            <PaginationController
              itemsCount={itemsCount}
              pageSizeLabel={t("organisation.manuscriptProcessing.perPage")}
            />
          </div>
        </>
      )}

      <ManuscriptProcessingReportSheet
        report={openReport}
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        onOpenOriginal={setOriginalEntryId}
      />
    </div>
  );
};
